import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { 
  Briefcase, Plus, Pencil, Trash2, Download, Upload, Building2, 
  Calendar, Clock, FileText, Archive, AlertCircle, CheckCircle2,
  User, Search, X, FileDown, History
} from 'lucide-react';
import api, { contratService } from '../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES_CONTRAT = [
  { value: 'CDI', label: 'CDI' },
  { value: 'CDD', label: 'CDD' },
  { value: 'Intérim', label: 'Intérim' },
  { value: 'Alternance', label: 'Alternance' },
  { value: 'Stage', label: 'Stage' },
  { value: 'Contrat Pro', label: 'Contrat Pro' }
];

const STATUTS_CONTRAT = [
  { value: 'en_cours', label: 'En cours', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'termine', label: 'Terminé', color: 'bg-slate-100 text-slate-700' },
  { value: 'rompu', label: 'Rompu', color: 'bg-red-100 text-red-700' },
  { value: 'renouvele', label: 'Renouvelé', color: 'bg-blue-100 text-blue-700' }
];

const MOTIFS_FIN = [
  { value: 'fin_normale', label: 'Fin normale du contrat' },
  { value: 'demission', label: 'Démission' },
  { value: 'licenciement', label: 'Licenciement' },
  { value: 'rupture_conventionnelle', label: 'Rupture conventionnelle' },
  { value: 'fin_periode_essai', label: 'Fin de période d\'essai' },
  { value: 'rupture_pe_beneficiaire', label: 'Rupture période d\'essai (bénéficiaire)' },
  { value: 'rupture_pe_employeur', label: 'Rupture période d\'essai (employeur)' },
  { value: 'mutation', label: 'Mutation' },
  { value: 'retraite', label: 'Départ retraite' },
  { value: 'autre', label: 'Autre' }
];

const ContratsManager = ({ userRole = 'admin', zone = null }) => {
  const [contrats, setContrats] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [showFinContratDialog, setShowFinContratDialog] = useState(false);
  const [showHistorique, setShowHistorique] = useState(null);
  const [editingContrat, setEditingContrat] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPJ, setUploadingPJ] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    apprenant_id: '',
    entreprise_id: '',
    type_contrat: 'CDD',
    poste: '',
    date_debut: '',
    date_fin: '',
    date_signature: new Date().toISOString().split('T')[0],
    duree_heures: '',
    salaire: '',
    horaires: '',
    notes: '',
    statut: 'en_cours',
    periode_essai_duree: ''
  });
  
  // Fin de contrat form
  const [finContratForm, setFinContratForm] = useState({
    date_fin: '',
    motif_fin: '',
    notes_fin: ''
  });

  // Création rapide d'entreprise depuis le formulaire contrat
  const [showQuickEntreprise, setShowQuickEntreprise] = useState(false);
  const [creatingEntreprise, setCreatingEntreprise] = useState(false);
  const [quickEntreprise, setQuickEntreprise] = useState({
    nom: '',
    secteur: '',
    email: '',
    telephone: ''
  });

  useEffect(() => {
    loadData();
  }, [zone]);

  const loadData = async () => {
    try {
      const [contratsRes, beneficiairesRes, entreprisesRes] = await Promise.all([
        api.get('/contrats'),
        api.get('/apprenants'),
        api.get('/entreprises')
      ]);
      setContrats(contratsRes.data || []);
      setBeneficiaires(beneficiairesRes.data || []);
      setEntreprises(entreprisesRes.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntreprise = async () => {
    if (!quickEntreprise.nom.trim()) {
      toast.error("Le nom de l'entreprise est obligatoire");
      return;
    }
    setCreatingEntreprise(true);
    try {
      const response = await api.post('/entreprises', {
        nom: quickEntreprise.nom.trim(),
        secteur: quickEntreprise.secteur.trim() || 'Non renseigné',
        email: quickEntreprise.email.trim() || null,
        telephone: quickEntreprise.telephone.trim()
      });
      const nouvelle = response.data;
      setEntreprises(prev => [...prev, nouvelle]);
      // Sélectionner automatiquement la nouvelle entreprise dans le contrat
      setFormData(prev => ({ ...prev, entreprise_id: nouvelle.id }));
      setQuickEntreprise({ nom: '', secteur: '', email: '', telephone: '' });
      setShowQuickEntreprise(false);
      toast.success(`Entreprise « ${nouvelle.nom} » créée et sélectionnée`);
    } catch (error) {
      console.error('Erreur création entreprise:', error);
      toast.error("Erreur lors de la création de l'entreprise");
    } finally {
      setCreatingEntreprise(false);
    }
  };

  const resetForm = () => {
    setFormData({
      apprenant_id: '',
      entreprise_id: '',
      type_contrat: 'CDD',
      poste: '',
      date_debut: '',
      date_fin: '',
      date_signature: new Date().toISOString().split('T')[0],
      duree_heures: '',
      salaire: '',
      horaires: '',
      notes: '',
      statut: 'en_cours',
      periode_essai_duree: ''
    });
    setEditingContrat(null);
  };

  const handleOpenDialog = (contrat = null) => {
    if (contrat) {
      setEditingContrat(contrat);
      setFormData({
        apprenant_id: contrat.apprenant_id || '',
        entreprise_id: contrat.entreprise_id || '',
        type_contrat: contrat.type_contrat || 'CDD',
        poste: contrat.poste || contrat.intitule_poste || '',
        date_debut: contrat.date_debut || '',
        date_fin: contrat.date_fin || '',
        date_signature: contrat.date_signature || '',
        duree_heures: contrat.duree_heures || '',
        salaire: contrat.salaire || '',
        horaires: contrat.horaires || '',
        notes: contrat.notes || contrat.commentaire || '',
        statut: contrat.statut || 'en_cours',
        periode_essai_duree: contrat.periode_essai_duree || ''
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.apprenant_id || !formData.entreprise_id || !formData.type_contrat) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        apprenant_id: formData.apprenant_id,
        entreprise_id: formData.entreprise_id,
        type_contrat: formData.type_contrat,
        poste: formData.poste,
        date_debut: formData.date_debut || null,
        date_fin: formData.date_fin || null,
        date_signature: formData.date_signature || null,
        duree_heures: formData.duree_heures ? parseInt(formData.duree_heures) : null,
        salaire: formData.salaire ? parseFloat(formData.salaire) : null,
        horaires: formData.horaires,
        notes: formData.notes,
        statut: formData.statut,
        periode_essai_duree: formData.periode_essai_duree ? parseInt(formData.periode_essai_duree) : null
      };

      if (editingContrat) {
        await api.put(`/contrats/${editingContrat.id}`, payload);
        toast.success('Contrat mis à jour');
      } else {
        await api.post('/contrats', payload);
        toast.success('Contrat créé');
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenFinContrat = (contrat) => {
    setEditingContrat(contrat);
    setFinContratForm({
      date_fin: contrat.date_fin || new Date().toISOString().split('T')[0],
      motif_fin: contrat.motif_fin || '',
      notes_fin: ''
    });
    setShowFinContratDialog(true);
  };

  const handleFinContrat = async () => {
    if (!finContratForm.date_fin || !finContratForm.motif_fin) {
      toast.error('Veuillez remplir la date et le motif de fin');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/contrats/${editingContrat.id}`, {
        date_fin: finContratForm.date_fin,
        motif_fin: finContratForm.motif_fin,
        notes: finContratForm.notes_fin ? 
          `${editingContrat.notes || ''}\n[FIN] ${finContratForm.notes_fin}` : 
          editingContrat.notes,
        statut: 'termine'
      });
      
      toast.success('Fin de contrat enregistrée');
      setShowFinContratDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (contratId) => {
    if (!window.confirm('Supprimer ce contrat ? Cette action est irréversible.')) return;
    
    try {
      await api.delete(`/contrats/${contratId}`);
      toast.success('Contrat supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUploadPJ = async (contratId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingPJ(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/contrats/${contratId}/pieces-jointes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document ajouté');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingPJ(false);
    }
  };

  const handleDownloadPJ = async (contratId, pjId, filename) => {
    try {
      const response = await api.get(`/contrats/${contratId}/pieces-jointes/${pjId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeletePJ = async (contratId, pjId) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    
    try {
      await api.delete(`/contrats/${contratId}/pieces-jointes/${pjId}`);
      toast.success('Document supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Helpers
  const getBeneficiaireName = (id) => {
    const ben = beneficiaires.find(b => b.id === id);
    return ben ? `${ben.prenom} ${ben.nom}` : 'Inconnu';
  };

  const getEntrepriseName = (id) => {
    const ent = entreprises.find(e => e.id === id);
    return ent?.nom || 'Inconnue';
  };

  const getStatutBadge = (statut) => {
    const s = STATUTS_CONTRAT.find(st => st.value === statut) || STATUTS_CONTRAT[0];
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  // Filtrage
  const filteredContrats = contrats.filter(c => {
    const matchSearch = search === '' || 
      getBeneficiaireName(c.apprenant_id).toLowerCase().includes(search.toLowerCase()) ||
      getEntrepriseName(c.entreprise_id).toLowerCase().includes(search.toLowerCase()) ||
      (c.poste || '').toLowerCase().includes(search.toLowerCase());
    
    const matchStatut = filterStatut === 'all' || c.statut === filterStatut;
    
    return matchSearch && matchStatut;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-blue-600" />
          Gestion des Contrats
        </h2>
        <Button 
          onClick={() => handleOpenDialog()} 
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
          data-testid="new-contrat-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Contrat
        </Button>
      </div>

      {/* Filtres */}
      <Card className="bg-white border-slate-200 rounded-2xl shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par bénéficiaire, entreprise, poste..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="search-contrats"
              />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[180px] rounded-xl" data-testid="filter-statut">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {STATUTS_CONTRAT.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des contrats */}
      {filteredContrats.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-8 shadow-sm">
          <CardContent className="text-center py-12">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun contrat trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredContrats.map((contrat) => (
            <Card key={contrat.id} className="bg-white border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <CardContent className="p-0">
                {/* En-tête du contrat */}
                <div className="p-5 border-b border-slate-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Badge type */}
                      <div className={`px-4 py-2 rounded-xl text-sm font-bold ${
                        contrat.type_contrat === 'CDI' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : contrat.type_contrat === 'CDD'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {contrat.type_contrat}
                        {contrat.duree_heures && ` (${contrat.duree_heures}h)`}
                      </div>
                      
                      {/* Infos principales */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-bold text-slate-900 text-lg">
                            {getBeneficiaireName(contrat.apprenant_id)}
                          </span>
                          {getStatutBadge(contrat.statut)}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Building2 className="w-4 h-4" />
                          <span>{getEntrepriseName(contrat.entreprise_id)}</span>
                          {contrat.poste && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span>{contrat.poste}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {contrat.statut === 'en_cours' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenFinContrat(contrat)}
                          className="rounded-xl text-amber-600 hover:bg-amber-50"
                          title="Terminer le contrat"
                          data-testid={`fin-contrat-${contrat.id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDialog(contrat)}
                        className="rounded-xl"
                        data-testid={`edit-contrat-${contrat.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(contrat.id)}
                        className="rounded-xl text-red-600 hover:bg-red-50"
                        data-testid={`delete-contrat-${contrat.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Détails du contrat */}
                <div className="p-5 bg-slate-50/50">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 block mb-1">Date signature</span>
                      <span className="font-medium text-slate-900">
                        {contrat.date_signature ? new Date(contrat.date_signature).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block mb-1">Date début</span>
                      <span className="font-medium text-slate-900">
                        {contrat.date_debut ? new Date(contrat.date_debut).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block mb-1">Date fin</span>
                      <span className="font-medium text-slate-900">
                        {contrat.date_fin ? new Date(contrat.date_fin).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block mb-1">Salaire</span>
                      <span className="font-bold text-emerald-600">
                        {contrat.salaire ? `${contrat.salaire.toLocaleString()}€/mois` : '-'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Motif de fin si terminé */}
                  {contrat.motif_fin && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="font-medium">Motif de fin :</span>
                        <span>{MOTIFS_FIN.find(m => m.value === contrat.motif_fin)?.label || contrat.motif_fin}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Horaires et notes */}
                  {(contrat.horaires || contrat.notes) && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {contrat.horaires && (
                        <div>
                          <span className="text-slate-500 text-sm block mb-1">Horaires</span>
                          <span className="text-slate-900">{contrat.horaires}</span>
                        </div>
                      )}
                      {contrat.notes && (
                        <div>
                          <span className="text-slate-500 text-sm block mb-1">Notes</span>
                          <span className="text-slate-900">{contrat.notes}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Pièces jointes */}
                <div className="p-5 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Pièces jointes
                    </span>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => handleUploadPJ(contrat.id, e)}
                        disabled={uploadingPJ}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <Button size="sm" variant="outline" className="rounded-xl" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-1" />
                          {uploadingPJ ? 'Upload...' : 'Ajouter'}
                        </span>
                      </Button>
                    </label>
                  </div>
                  
                  {contrat.pieces_jointes?.length > 0 ? (
                    <div className="space-y-2">
                      {contrat.pieces_jointes.map((pj) => (
                        <div key={pj.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-slate-700">{pj.nom_fichier}</span>
                            <span className="text-xs text-slate-400">
                              {new Date(pj.uploaded_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadPJ(contrat.id, pj.id, pj.nom_fichier)}
                              className="text-blue-600 hover:bg-blue-50"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeletePJ(contrat.id, pj.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">Aucune pièce jointe</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog création/édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              {editingContrat ? 'Modifier le contrat' : 'Nouveau contrat'}
            </DialogTitle>
            <DialogDescription>
              {editingContrat ? 'Modifiez les informations du contrat' : 'Enregistrez un nouveau contrat de travail'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Bénéficiaire & Entreprise */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bénéficiaire *</Label>
                <Select 
                  value={formData.apprenant_id} 
                  onValueChange={(val) => setFormData({...formData, apprenant_id: val})}
                  disabled={!!editingContrat}
                >
                  <SelectTrigger className="rounded-xl" data-testid="select-beneficiaire">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {beneficiaires.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.prenom} {b.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entreprise *</Label>
                <Select
                  value={formData.entreprise_id}
                  onValueChange={(val) => setFormData({...formData, entreprise_id: val})}
                >
                  <SelectTrigger className="rounded-xl" data-testid="select-entreprise">
                    <SelectValue placeholder={entreprises.length === 0 ? 'Aucune entreprise — créez-en une' : 'Sélectionner'} />
                  </SelectTrigger>
                  <SelectContent>
                    {entreprises.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        Aucune entreprise enregistrée
                      </div>
                    ) : (
                      entreprises.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setShowQuickEntreprise(!showQuickEntreprise)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  data-testid="toggle-quick-entreprise"
                >
                  <Plus className="w-3 h-3" />
                  {showQuickEntreprise ? 'Annuler la création' : 'Créer une nouvelle entreprise'}
                </button>
              </div>
            </div>

            {/* Création rapide d'entreprise */}
            {showQuickEntreprise && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl space-y-3">
                <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Nouvelle entreprise
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nom *</Label>
                    <Input
                      value={quickEntreprise.nom}
                      onChange={(e) => setQuickEntreprise({ ...quickEntreprise, nom: e.target.value })}
                      placeholder="Ex : Carrefour Martinique"
                      className="rounded-xl"
                      data-testid="quick-entreprise-nom"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Secteur d'activité</Label>
                    <Input
                      value={quickEntreprise.secteur}
                      onChange={(e) => setQuickEntreprise({ ...quickEntreprise, secteur: e.target.value })}
                      placeholder="Ex : Grande distribution"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={quickEntreprise.email}
                      onChange={(e) => setQuickEntreprise({ ...quickEntreprise, email: e.target.value })}
                      placeholder="contact@entreprise.fr"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Téléphone</Label>
                    <Input
                      value={quickEntreprise.telephone}
                      onChange={(e) => setQuickEntreprise({ ...quickEntreprise, telephone: e.target.value })}
                      placeholder="0596 ..."
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleCreateEntreprise}
                  disabled={creatingEntreprise || !quickEntreprise.nom.trim()}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                  data-testid="quick-entreprise-submit"
                >
                  {creatingEntreprise ? 'Création...' : "Créer et sélectionner"}
                </Button>
              </div>
            )}

            {/* Type & Statut */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de contrat *</Label>
                <Select 
                  value={formData.type_contrat} 
                  onValueChange={(val) => setFormData({...formData, type_contrat: val})}
                >
                  <SelectTrigger className="rounded-xl" data-testid="select-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_CONTRAT.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select 
                  value={formData.statut} 
                  onValueChange={(val) => setFormData({...formData, statut: val})}
                >
                  <SelectTrigger className="rounded-xl" data-testid="select-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUTS_CONTRAT.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Poste */}
            <div className="space-y-2">
              <Label>Intitulé du poste</Label>
              <Input
                value={formData.poste}
                onChange={(e) => setFormData({...formData, poste: e.target.value})}
                placeholder="Ex: Agent de production, Technicien maintenance..."
                className="rounded-xl"
                data-testid="input-poste"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date de signature</Label>
                <Input
                  type="date"
                  value={formData.date_signature}
                  onChange={(e) => setFormData({...formData, date_signature: e.target.value})}
                  className="rounded-xl"
                  data-testid="input-date-signature"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={formData.date_debut}
                  onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                  className="rounded-xl"
                  data-testid="input-date-debut"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin {formData.type_contrat !== 'CDI' && '(prévue)'}</Label>
                <Input
                  type="date"
                  value={formData.date_fin}
                  onChange={(e) => setFormData({...formData, date_fin: e.target.value})}
                  className="rounded-xl"
                  data-testid="input-date-fin"
                />
              </div>
            </div>

            {/* Durée & Salaire & Horaires */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Durée (heures)</Label>
                <Input
                  type="number"
                  value={formData.duree_heures}
                  onChange={(e) => setFormData({...formData, duree_heures: e.target.value})}
                  placeholder="Ex: 1607"
                  className="rounded-xl"
                  data-testid="input-duree"
                />
              </div>
              <div className="space-y-2">
                <Label>Salaire (€/mois)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.salaire}
                  onChange={(e) => setFormData({...formData, salaire: e.target.value})}
                  placeholder="Ex: 1800"
                  className="rounded-xl"
                  data-testid="input-salaire"
                />
              </div>
              <div className="space-y-2">
                <Label>Horaires</Label>
                <Input
                  value={formData.horaires}
                  onChange={(e) => setFormData({...formData, horaires: e.target.value})}
                  placeholder="Ex: 35h/semaine"
                  className="rounded-xl"
                  data-testid="input-horaires"
                />
              </div>
            </div>

            {/* Période d'essai */}
            <div className="space-y-2">
              <Label>Période d'essai (jours)</Label>
              <Input
                type="number"
                value={formData.periode_essai_duree}
                onChange={(e) => setFormData({...formData, periode_essai_duree: e.target.value})}
                placeholder="Ex: 60 (laisser vide si pas de période d'essai)"
                className="rounded-xl"
                data-testid="input-periode-essai"
              />
              <p className="text-xs text-slate-500">
                Durées courantes: 30 jours (1 mois), 60 jours (2 mois), 90 jours (3 mois)
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes / Commentaires</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Informations complémentaires..."
                className="rounded-xl min-h-[80px]"
                data-testid="input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDialog(false)} 
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
              data-testid="save-contrat-btn"
            >
              {submitting ? 'Enregistrement...' : (editingContrat ? 'Mettre à jour' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog fin de contrat */}
      <Dialog open={showFinContratDialog} onOpenChange={setShowFinContratDialog}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Archive className="w-5 h-5" />
              Terminer le contrat
            </DialogTitle>
            <DialogDescription>
              Enregistrez la fin du contrat avec le motif et la date de fin effective
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date de fin *</Label>
              <Input
                type="date"
                value={finContratForm.date_fin}
                onChange={(e) => setFinContratForm({...finContratForm, date_fin: e.target.value})}
                className="rounded-xl"
                data-testid="input-date-fin-contrat"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Motif de fin *</Label>
              <Select 
                value={finContratForm.motif_fin} 
                onValueChange={(val) => setFinContratForm({...finContratForm, motif_fin: val})}
              >
                <SelectTrigger className="rounded-xl" data-testid="select-motif-fin">
                  <SelectValue placeholder="Sélectionner le motif" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIFS_FIN.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Commentaire (optionnel)</Label>
              <Textarea
                value={finContratForm.notes_fin}
                onChange={(e) => setFinContratForm({...finContratForm, notes_fin: e.target.value})}
                placeholder="Précisions sur la fin du contrat..."
                className="rounded-xl"
                data-testid="input-notes-fin"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowFinContratDialog(false)} 
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleFinContrat} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 rounded-xl"
              data-testid="confirm-fin-contrat-btn"
            >
              {submitting ? 'Enregistrement...' : 'Confirmer la fin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContratsManager;
